export type AvailabilityStatus =
  | 'available'
  | 'low'
  | 'full'
  | 'annotated'
  | 'season_closed'
  | 'outside_window'
  | 'lead_time'

export interface AvailabilityCalendarDay {
  /** Date ISO `yyyy-MM-dd`. */
  date: string
  status: AvailabilityStatus
  /** Titre visible (texte = statut, pas seulement couleur - RGAA). i18n au point d'appel. */
  label: string
  /** Whether the day can be picked as a stay boundary (available days only). */
  selectable?: boolean
  /** Reason text shown when an annotated day is activated (modal/popover). */
  noteText?: string
  /** High-season period for season_closed deep-link (CAL-B2). */
  periodId?: string
  /** Slot booking: shared id for consecutive nights in the same availability period. */
  slotId?: string
  /** CAL-B3 - join waiting queue for this slot (mode slot, day full). */
  queueSlotId?: string
  /** CAL-B3 - nightly property-date waiting queue (Y-m-d). */
  queueTargetDate?: string
  /** Per-day post-subsidy price short label (CR-14-PRICE); overrides flat nightlyPrice. */
  nightlyPriceShort?: string
  /** Parsed EUR major units for stay totals when nightly_price_cents is present. */
  nightlyPriceValue?: number
}

export interface AvailabilityLegendItem {
  status: AvailabilityStatus
  label: string
}

/** Visible legend: binary available / unavailable (detail stays on hover + aria). */
export const availabilityLegendOrder = ['available', 'unavailable'] as const

export type AvailabilityLegendKey = (typeof availabilityLegendOrder)[number]

/** True when bookable days expose at least two distinct nightly amounts. */
export function hasVariableNightlyPrices(days: AvailabilityCalendarDay[] | null | undefined): boolean {
  if (!Array.isArray(days) || days.length < 2) return false

  const numeric = new Set<number>()
  const labels = new Set<string>()

  for (const d of days) {
    if (d == null || (d.status !== 'available' && d.status !== 'low')) continue

    if (typeof d.nightlyPriceValue === 'number' && Number.isFinite(d.nightlyPriceValue)) {
      numeric.add(d.nightlyPriceValue)
      if (numeric.size > 1) return true
      continue
    }

    if (typeof d.nightlyPriceShort === 'string') {
      const short = d.nightlyPriceShort.trim()
      if (short !== '') {
        labels.add(short)
        if (labels.size > 1) return true
      }
    }
  }

  // Prefer numeric comparison; fall back to short labels only when no numeric values exist.
  if (numeric.size > 0) return numeric.size > 1
  return labels.size > 1
}
